import { useState, useRef, useEffect } from "react";
import { FilterDateRange } from "../interfaces";
import { FilterInfo } from "@/store/deduction/deductionFilter";
import { ApprovalFilterInfo } from "@/store/approvals";
import { AnalyticsFilterInfo } from "@/store/analytics";
import { AccountingRemittanceFilterInfo } from "@/store/accounting";

interface UseDateRangeStateProps {
  filterInfo: FilterInfo | ApprovalFilterInfo | AnalyticsFilterInfo | AccountingRemittanceFilterInfo;
  defaultCheckDateRange: FilterDateRange | null;
  isFilterApplied: boolean;
}

/**
 * Manages local state for date range selectors to ensure instant UI updates
 * Only uses default checkDateRange when filter is not applied (initial state)
 */
export const useDateRangeState = ({ filterInfo, defaultCheckDateRange, isFilterApplied }: UseDateRangeStateProps) => {
  // Only use default if filter is not applied (initial state)
  // If filter is applied and checkDateRange is null, respect that null
  const initialCheckDateRange = isFilterApplied
    ? (filterInfo.checkDateRange as FilterDateRange | null)
    : ((filterInfo.checkDateRange ?? defaultCheckDateRange) as FilterDateRange | null);

  const [checkDateRangeValue, setCheckDateRangeValue] = useState<FilterDateRange | null>(initialCheckDateRange);
  const [invoiceDateRangeValue, setInvoiceDateRangeValue] = useState<FilterDateRange | null>(
    ("invoiceDateRange" in filterInfo ? filterInfo.invoiceDateRange : null) as FilterDateRange | null
  );
  const isUserUpdateRef = useRef(false);

  // Sync state with filterInfo changes (only when not user-initiated)
  useEffect(() => {
    // Only use default if filter is not applied (initial state)
    const effectiveCheckDateRange = isFilterApplied
      ? (filterInfo.checkDateRange as FilterDateRange | null)
      : ((filterInfo.checkDateRange ?? defaultCheckDateRange) as FilterDateRange | null);

    if (!isUserUpdateRef.current) {
      setTimeout(() => {
        setCheckDateRangeValue(prev => (prev === effectiveCheckDateRange ? prev : effectiveCheckDateRange));
        setInvoiceDateRangeValue(prev => {
          const newValue = ("invoiceDateRange" in filterInfo ? filterInfo.invoiceDateRange : null) as FilterDateRange | null;
          return prev === newValue ? prev : newValue;
        });
      }, 0);
    }
    isUserUpdateRef.current = false;
  }, [
    filterInfo.checkDateRange,
    "invoiceDateRange" in filterInfo ? filterInfo.invoiceDateRange : null,
    defaultCheckDateRange,
    isFilterApplied,
  ]);

  const updateCheckDateRange = (value: FilterDateRange | null) => {
    isUserUpdateRef.current = true;
    setCheckDateRangeValue(value);
  };

  const updateInvoiceDateRange = (value: FilterDateRange | null) => {
    isUserUpdateRef.current = true;
    setInvoiceDateRangeValue(value);
  };

  const resetToDefaults = (defaultCheckDateRange: FilterDateRange | null) => {
    setCheckDateRangeValue(defaultCheckDateRange);
    setInvoiceDateRangeValue(null);
  };

  return {
    checkDateRangeValue,
    invoiceDateRangeValue,
    updateCheckDateRange,
    updateInvoiceDateRange,
    resetToDefaults,
  };
};
